import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useApp } from '../context/AppContext';
import { t } from '../i18n/translations';

const styles = {
  nav: { background: 'var(--nav-bg)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  logo: { color: 'var(--nav-text)', fontSize: 22, fontWeight: 700, textDecoration: 'none' },
  right: { display: 'flex', alignItems: 'center', gap: 16 },
  navLink: { color: 'var(--nav-link)', fontSize: 14, fontWeight: 600, textDecoration: 'none' },
  btn: { background: '#fff', color: 'var(--primary)', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  avatar: { width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--nav-link)' },
  avatarInitials: { width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-dark)', border: '2px solid var(--nav-link)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' },
  greeting: { color: 'var(--nav-link)', fontSize: 14 },
  bellWrap: { position: 'relative', cursor: 'pointer' },
  bellBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: '#fff', padding: 4, position: 'relative' },
  badge: { position: 'absolute', top: -4, right: -4, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  dropdown: { position: 'absolute', top: 42, right: 0, background: 'var(--surface)', borderRadius: 14, boxShadow: 'var(--shadow-lg)', width: 340, zIndex: 200, overflow: 'hidden' },
  dropHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border)' },
  dropTitle: { fontWeight: 700, color: 'var(--heading)', fontSize: 15 },
  dropAction: { color: 'var(--primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'none' },
  notifItem: { padding: '12px 18px', borderBottom: '1px solid var(--surface2)', cursor: 'pointer' },
  notifMsg: { fontSize: 13, color: 'var(--text2)', marginBottom: 3 },
  notifTime: { fontSize: 11, color: 'var(--text4)' },
  emptyNotif: { padding: '24px 18px', textAlign: 'center', color: 'var(--text4)', fontSize: 14 },
  clearBtn: { width: '100%', padding: '12px', border: 'none', background: 'var(--surface2)', color: 'var(--text3)', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function timeAgo(date) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Navbar({ user, onLogout }) {
  const { language } = useApp();
  const T = (key) => t(language, key);
  const [notifications, setNotifications] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false); };
    const handleKey = (e) => { if (e.key === 'Escape') setShowDrop(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      fetchNotifications();
    } catch {}
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications/clear');
      setNotifications([]);
      setShowDrop(false);
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      fetchNotifications();
    } catch {}
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <nav style={styles.nav} className="nav nav-wrap">
      <Link to="/" style={styles.logo}>{T('appName')}</Link>
      <div style={styles.right} className="nav-right">
        {user.photo
          ? <img src={`${user.photo}`} alt={user.name} style={styles.avatar} />
          : <div style={styles.avatarInitials}>{getInitials(user.name)}</div>
        }
        <span style={styles.greeting} className="hide-mobile">{T('hello')}, {user.name}</span>
        <Link to="/appointments" style={styles.navLink}>{T('appointments')}</Link>
        <Link to="/settings" style={styles.navLink}>{T('settings')}</Link>

        {/* Notification Bell */}
        <div style={styles.bellWrap} ref={dropRef}>
          <button style={styles.bellBtn} onClick={() => setShowDrop(v => !v)}
            aria-label={unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'}
            aria-expanded={showDrop}>
            🔔
            {unreadCount > 0 && <span style={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
          </button>

          {showDrop && (
            <div style={styles.dropdown}>
              <div style={styles.dropHeader}>
                <span style={styles.dropTitle}>{T('notifications')} {unreadCount > 0 && `(${unreadCount})`}</span>
                {unreadCount > 0 && <button style={styles.dropAction} onClick={markAllRead}>{T('markAllRead')}</button>}
              </div>
              {notifications.length === 0 ? (
                <div style={styles.emptyNotif}>{T('noNotifications')}</div>
              ) : (
                <>
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {notifications.slice(0, 20).map(n => (
                      <div key={n._id} style={{ ...styles.notifItem, background: n.read ? 'var(--surface)' : 'var(--primary-light)' }}
                        onClick={() => markRead(n._id)}>
                        <div style={styles.notifMsg}>{n.message}</div>
                        <div style={styles.notifTime}>{timeAgo(n.createdAt)}</div>
                      </div>
                    ))}
                  </div>
                  <button style={styles.clearBtn} onClick={clearAll}>{T('clearAll')}</button>
                </>
              )}
            </div>
          )}
        </div>

        <button style={styles.btn} onClick={onLogout}>{T('logout')}</button>
      </div>
    </nav>
  );
}
